import * as Sentry from '@sentry/capacitor';
import { Injectable } from '@angular/core';

import { AnalyticsEvent, FirebaseErrorLogger } from '@guardfinder/analytics';

/**
 * A service used to log errors to Sentry. This class extends the base functionality
 * of `FirebaseErrorLogger` and provides a specific implementation for error logging.
 *
 * It is designed to handle errors while logging events and sends the error details
 * to Sentry for monitoring and debugging. This class leverages Angular's `@Injectable` decorator
 * to allow it to be provided and used across the application at the root level.
 */
@Injectable({
    providedIn: 'root'
})
export class SentryFirebaseErrorLogger extends FirebaseErrorLogger {
    constructor() {
        super();
    }

    /**
     * Logs an error to Sentry along with details of the event that caused the error.
     *
     * @param event The analytics event that was being processed when the error occurred
     * @param error The error that occurred during event processing
     * @returns A Promise that resolves when the error has been logged
     */
    override logError(event: AnalyticsEvent, error: unknown): Promise<void> {
        // Add event information as extra context
        Sentry.setExtra('eventName', event.name);
        if (event.params) {
            Sentry.setExtra('eventParams', event.params);
        }

        // Capture the error in Sentry
        Sentry.captureException(error);

        // Additionally, log the error to the console
        console.error('Error logging event:', event, error);

        return Promise.resolve();
    }
}
